import React from 'react';
import Flexbox from 'flexbox-react';
import Pagination from 'react-js-pagination';
import Avatar from 'react-avatar';
import { Tooltip, Position } from '@blueprintjs/core';


const ReviewerChip = ({
  reviewer,
  type,
  removable = false,
  onRemove,
}) => {
  const handleRemove = (event) => {
    event.stopPropagation();
    if (onRemove) {
      onRemove(reviewer);
    }
  };

  return (
    <div className={`assign-emp-reviewer-chip ${type || ''}`}>
      <Avatar
        name={reviewer.name}
        src={reviewer.image_file_name}
        size={20}
        round={true}
      />

      <span className="assign-emp-reviewer-name">
        {reviewer.name}
      </span>

      {removable && (
        <Tooltip content={`Remove ${reviewer.name}`} position={Position.TOP} portalContainer={document.body}>
          <button
            type="button"
            className="assign-emp-reviewer-remove-btn"
            onClick={handleRemove}
            aria-label={`Remove ${reviewer.name}`}
          >
            ×
          </button>
        </Tooltip>
      )}
    </div>
  );
};

const AddReviewerButton = ({ onClick,tooltipText = "Add reviewer"}) => (
  <Tooltip
    content={tooltipText}
    position={Position.TOP}
    portalContainer={document.body}
  >
    <span
      className="assign-emp-add-reviewer-tooltip-target"
    >
      <button
        type="button"
        className="assign-emp-add-reviewer-btn"
        onClick={onClick}
        aria-label={tooltipText}
      >
        +
      </button>
    </span>
  </Tooltip>
);

const AssignEmployeesTable = ({
  participants = [],
  raterColumns = [],
  isLoading,
  currentPage,
  perPage,
  totalParticipants,
  totalParticipantPages,
  onPageChange,
  onPerPageChange,
  onAddReviewer,
  onRemoveReviewer,
  onSendReminder,
  isSendingReminder = {},
  reminderCooldown = {},
}) => {
  const handleAddReviewer = (employee, type) => {
    if (onAddReviewer) {
      onAddReviewer(employee, type);
    }
  };

  const handleRemoveReviewer = (
    employee,
    reviewer,
    type
  ) => {
    if (onRemoveReviewer) {
      onRemoveReviewer(employee, reviewer, type);
    }
  };

  const handleSendReminder = (employee) => {
    if (onSendReminder) {
      onSendReminder(employee);
    }
  };

  const renderPagination = () => {
    if (!totalParticipants) {
      return null;
    }

    return (
      <div className="assign-emp-table-footer">
        <Flexbox
          justifyContent="space-between"
          alignItems="center"
          flexDirection="row"
        >
          <Flexbox justifyContent="flex-start">
            <Pagination
              activePage={currentPage}
              itemsCountPerPage={perPage}
              totalItemsCount={totalParticipants}
              pageRangeDisplayed={Math.min(
                5,
                totalParticipantPages || 5
              )}
              onChange={onPageChange}
              disabledClass="bp3-disabled"
              innerClass="bp3-button-group bp3-minimal bp3-pagination"
              itemClass="bp3-button"
              activeClass="bp3-active bp3-pagination-active"
              linkClass="no-underline"
              prevPageText={
                <span className="bp3-icon-chevron-left" />
              }
              nextPageText={
                <span className="bp3-icon-chevron-right" />
              }
              firstPageText={
                <span className="bp3-icon-double-chevron-left" />
              }
              lastPageText={
                <span className="bp3-icon-double-chevron-right" />
              }
            />
          </Flexbox>

          <Flexbox alignItems="center">
            <span
              className="assign-emp-footer-text"
              style={{ marginRight: 8 }}
            >
              Show
            </span>
            <select
              value={perPage}
              onChange={onPerPageChange}
              className="bp3-input"
              style={{
                width: 80,
                marginRight: 8,
              }}
            >
              {[10, 25, 40, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>

            <span className="assign-emp-footer-text">
              per page
            </span>
          </Flexbox>
        </Flexbox>
      </div>
    );
  };

  return (
    <div className="assign-emp-participants-card">

      {/* Table Header */}

      <div className="assign-emp-table-scroll">

      <div className="assign-emp-table-header">
        <div className="assign-emp-fixed-employee">
          Employee
        </div>

        {raterColumns.map((rater) => (
          <div
            className="assign-emp-rater-header"
            key={rater.id}
          >
            {rater.title}
          </div>
        ))}

        <div className="assign-emp-fixed-action">
          Action
        </div>

      </div>

      {/* Table Body */}

      <div className="assign-emp-table-body">

      {isLoading ? (
        <div className="assign-emp-empty-state">
          Loading participants...
        </div>
      ) : participants.length === 0 ? (
        <div className="assign-emp-empty-state">
          No participants found
        </div>
      ) : (
        participants.map((employee) => (


          <div className="assign-emp-table-row" key={employee.id}>

            {/* Employee */}
            <div className="assign-emp-fixed-employee assign-emp-cell">
              <div className="assign-emp-avatar-wrapper">
                <Avatar
                  name={employee.user?.full_name}
                  src={employee.user?.image_file_name}
                  size={36}
                  round={true}
                />
              </div>

              <div className="assign-emp-info">
                <div className="assign-emp-name">
                  {employee.user?.full_name}
                </div>

                <div className="assign-emp-email">
                  {employee.user?.email}
                </div>
              </div>
            </div>

            {/* Dynamic raters */}
            {raterColumns.map(raterColumn => {
              const participantRater = (employee.raters || []).find(
                rater => rater.id === raterColumn.id
              );

              const assignees = participantRater?.assignees || [];

              return (
                <div className="assign-emp-rater-cell" key={raterColumn.id}>
                  {assignees.map(reviewer => (
                    <ReviewerChip
                      key={reviewer.id}
                      reviewer={{
                        id: reviewer.id,
                        image_file_name:
                          reviewer.image_file_name,
                        name: reviewer.full_name,
                      }}
                      type={raterColumn.title}
                      removable={raterColumn.title !== "Self"}
                      onRemove={() =>
                        onRemoveReviewer(
                          employee,
                          reviewer,
                          participantRater
                        )
                      }
                    />
                  ))}

                  {raterColumn.title !== "Self" && (
                    <AddReviewerButton
                      tooltipText={`Add ${raterColumn.title}`}
                      onClick={() =>
                        onAddReviewer(
                          employee,
                          participantRater
                        )
                      }
                    />)}
                </div>
              );
            })}


            {/* Action */}
            <div className="assign-emp-fixed-action assign-emp-reminder-cell">
              <button
                type="button"
                className="assign-emp-reminder-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendReminder(employee);
                }}
                disabled={
                  isSendingReminder[employee.user?.id] ||
                  reminderCooldown[employee.user?.id]
                }
              >
                {isSendingReminder[employee.user?.id]
                  ? "Sending..."
                  : reminderCooldown[employee.user?.id]
                    ? "Wait 5s"
                    : "Send Reminder"}
              </button>
            </div>

          </div>
        ))
      )}

      </div>

      </div>

      {/* Pagination */}
      {!isLoading &&
        participants.length > 0 &&
        renderPagination()}
    </div>
  );
};

export default AssignEmployeesTable;